'use client';

import * as DS from '@aurorasolar/ds';

import { useCurrency } from '@/hooks/useCurrency';

import { ProjectionResult, ScenarioRange } from '../engine';

import { roundFive, roundHundred } from './chartFormat';
import { ReadoutStrip } from './ReadoutStrip';

type Props = {
  result: ProjectionResult;
  scenarioRange: ScenarioRange;
};

// Every view's escalator is flat, so the cheapest view is the cheapest in every
// projected year — summing the band's edges gives the calmest and hottest
// views' own 20-year totals, not a blend of them.
const envelopeTotals = ({ byYear }: ScenarioRange) =>
  Object.values(byYear).reduce(
    (totals, { highUsd, lowUsd }) => ({ highUsd: totals.highUsd + highUsd * 12, lowUsd: totals.lowUsd + lowUsd * 12 }),
    { highUsd: 0, lowUsd: 0 }
  );

// The bill's own conclusion. Nothing here is a solar number: the final monthly
// bill is the last step of the plotted curve, and the total is its integral.
export const BillTotals = ({ result, scenarioRange }: Props) => {
  const { formatCurrency } = useCurrency();
  const { years } = result;

  const final = years[years.length - 1];
  const finalMonthlyUsd = (final.doNothingUsd - years[years.length - 2].doNothingUsd) / 12;
  // Keyed off the year this section actually names, not the global horizon
  // constant — the two agree only while every state's rate series ends together
  const monthlySpread = scenarioRange.byYear[final.calendarYear];
  const totals = envelopeTotals(scenarioRange);

  return (
    <DS.Flex aria-label="What that adds up to" as="section" gap={4} textAlign="left">
      <DS.Text color="uiHelperCopy" letterSpacing="0.08em" text="body12">
        WHAT THAT ADDS UP TO
      </DS.Text>

      <ReadoutStrip
        cells={[
          { label: `Your bill in ${final.calendarYear}`, value: `~${formatCurrency(roundFive(finalMonthlyUsd))}/mo` },
          {
            label: `Paid to the utility, ${years[1].calendarYear}–${final.calendarYear}`,
            value: formatCurrency(roundHundred(final.doNothingUsd)),
          },
        ]}
      />

      <DS.Text as="p" color="uiHelperCopy" text="body14">
        Rates could climb slower or faster: across all three views your {final.calendarYear} bill lands between{' '}
        {formatCurrency(roundFive(monthlySpread.lowUsd))} and {formatCurrency(roundFive(monthlySpread.highUsd))} a
        month, and the 20-year total between {formatCurrency(roundHundred(totals.lowUsd))} and{' '}
        {formatCurrency(roundHundred(totals.highUsd))}.
      </DS.Text>

      <DS.Text as="p" color="uiHelperCopy" text="body12">
        A simulation of state-average rates, not a bill from your utility.
      </DS.Text>
    </DS.Flex>
  );
};
